import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Un modal pour sélectionner plusieurs appareils (pin codes) dans une liste.
 *
 * PROPS:
 * isVisible (boolean): Contrôle l'affichage du modal.
 * onClose (function): Appelé pour fermer le modal.
 * onSubmit (function): Appelé avec un tableau de pin codes lors de la validation.
 */
const MultiDeviceSelector = ({ isVisible, onClose, onSubmit, currentPinCode }) => {
    const [allDevices, setAllDevices] = useState([]);
    const [selectedPins, setSelectedPins] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Charger les appareils au montage
    useEffect(() => {
        if (isVisible) {
            setIsLoading(true);
            axios.get(`${process.env.REACT_APP_IP}:${process.env.REACT_APP_PORT}/InterfaceTizenDevice`)
                .then(res => {
                    setAllDevices(res.data);
                })
                .catch(err => console.log(err))
                .finally(() => setIsLoading(false));
        }
    }, [isVisible]); // Re-charger si le modal est ré-ouvert

    // Gérer la fermeture du modal
    const handleClose = (e) => {
        if (e.target.id === "wrapper") onClose();
    };

    // Filtrer les appareils en fonction de la recherche
    const filteredDevices = allDevices.filter(device =>
        device._pin_code !== currentPinCode &&
        `${device._client_name} ${device._app_type} ${device._pin_code}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
    );

    // Gérer le clic sur une checkbox
    const handleToggleSelect = (pinCode) => { 
        setSelectedPins(prevPins => {
            if (prevPins.includes(pinCode)) {
                // Si déjà sélectionné, le retirer
                return prevPins.filter(pin => pin !== pinCode);
            } else {
                // Sinon, l'ajouter
                return [...prevPins, pinCode];
            }
        });
    };
    

    // Gérer le "Sélectionner tout"
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // Sélectionner tous les appareils *filtrés*
            setSelectedPins(filteredDevices.map(d => d._pin_code));
        } else {
            // Tout désélectionner
            setSelectedPins([]);
        }
    };

    // Gérer la soumission
    const handleSubmit = () => {
        onSubmit(selectedPins); // Renvoie le tableau de PINs
        onClose(); // Ferme le modal
    };

    // Calculer l'état du "Select All" (indeterminate/checked)
    const isAllSelected = filteredDevices.length > 0 && selectedPins.length === filteredDevices.length;
    const isIndeterminate = selectedPins.length > 0 && selectedPins.length < filteredDevices.length;


    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm flex justify-center items-center" id="wrapper" onClick={handleClose}>
            <div className="bg-white rounded-lg p-6 w-[92vw] max-w-[800px] flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">Sélectionner des appareils</h2>
                    <button className="text-black text-xl" onClick={onClose}>X</button>
                </div>

                <input
                    type="text"
                    placeholder="Rechercher un client, type d'app ou pin code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mb-4 w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <div className="overflow-auto max-h-[400px]">
                    {isLoading ? (
                        <div className="text-center p-4">Chargement...</div>
                    ) : (
                        <table className="min-w-full text-sm text-left text-slate-900 border">
                            <thead className="bg-slate-200 sticky top-0">
                                <tr>
                                    <th className="px-4 py-2 w-10">
                                        <input
                                            type="checkbox"
                                            checked={isAllSelected}
                                            ref={el => el && (el.indeterminate = isIndeterminate)} // Gère l'état "intermédiaire"
                                            onChange={handleSelectAll}
                                            className="form-checkbox h-4 w-4"
                                        />
                                    </th>
                                    <th className="px-4 py-2">Nom Client</th>
                                    <th className="px-4 py-2">Type App</th>
                                    <th className="px-4 py-2">Pin Code</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDevices.map((device, idx) => (
                                    <tr 
                                        key={idx} 
                                        className={`border-t hover:bg-slate-100 ${selectedPins.includes(device._pin_code) ? 'bg-blue-50' : ''}`}
                                        onClick={() => handleToggleSelect(device._pin_code)} // Permet de cliquer sur la ligne
                                    >
                                        <td className="px-4 py-2">
                                            <input
                                                type="checkbox"
                                                checked={selectedPins.includes(device._pin_code)}
                                                onChange={() => handleToggleSelect(device._pin_code)} // Permet de cliquer sur la checkbox
                                                className="form-checkbox h-4 w-4"
                                                onClick={(e) => e.stopPropagation()} // Empêche le clic de se propager à la ligne (double-toggle)
                                            />
                                        </td>
                                        <td className="px-4 py-2">{device._client_name}</td>
                                        <td className="px-4 py-2">{device._app_type}</td>
                                        <td className="px-4 py-2">{device._pin_code}</td>
                                    </tr>
                                ))}
                                {filteredDevices.length === 0 && !isLoading && (
                                    <tr>
                                        <td colSpan="4" className="text-center py-4 text-slate-500">Aucun résultat trouvé</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="mt-6 flex justify-between items-center">
                    <span className="text-sm text-slate-600">
                        {selectedPins.length} appareil(s) sélectionné(s)
                    </span>
                    <button
                        onClick={handleSubmit}
                        className="text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-sm font-medium disabled:bg-slate-400"
                    >
                        Valider la sélection
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MultiDeviceSelector;